import type { Coordinates } from "@bounce/google-place"
import type { Nullable } from "@bounce/util"

import type { GetNearbyStorageSpotsQueryVariables } from "@/libs/bounce/bounceSdk.types.gen"
import { StoreOrderBy, ReviewOrderByField, SortOrderDirection } from "@/libs/bounce/bounceSdk.types.gen"
import { getLocaleFromLanguage } from "@/utils/i18n/locale"

const getCoordinates = ({
  coordinates,
  cityId,
  citySlug,
  poiSlug,
}: Pick<GetNearbyStorageSpotsQueryVariables, "coordinates" | "cityId" | "citySlug" | "poiSlug">) => {
  if (coordinates?.latitude && coordinates.longitude) {
    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    }
  }

  if (!cityId && !citySlug && !poiSlug) {
    return {
      latitude: 0,
      longitude: 0,
    }
  }

  return undefined
}

export type GetNearbyStorageSpotsQueryVarsOptions = Pick<
  GetNearbyStorageSpotsQueryVariables,
  | "after"
  | "cityId"
  | "citySlug"
  | "country"
  | "distance"
  | "first"
  | "firstPois"
  | "marketingPageEnabled"
  | "poiSlug"
  | "types"
  | "withReservationCount"
  | "isOpen247"
  | "initialSuggestions"
  | "minRating"
  | "orderBy"
> & {
  coordinates?: Nullable<Coordinates>
  firstReviews?: GetNearbyStorageSpotsQueryVariables["firstReviews"]
  minReviewsFeedbackLength?: GetNearbyStorageSpotsQueryVariables["minReviewsFeedbackLength"]
  minReviewsRating?: GetNearbyStorageSpotsQueryVariables["minReviewsRating"]
  language: string
}

export const getNearbyStorageSpotsQueryVars = ({
  after,
  cityId,
  citySlug,
  coordinates,
  country,
  distance,
  first,
  firstPois,
  firstReviews,
  language,
  marketingPageEnabled,
  minReviewsRating,
  poiSlug,
  types,
  withReservationCount,
  minReviewsFeedbackLength,
  isOpen247,
  minRating,
  orderBy,
  initialSuggestions,
}: GetNearbyStorageSpotsQueryVarsOptions): GetNearbyStorageSpotsQueryVariables => ({
  ...(after ? { after } : {}),
  ...(cityId ? { cityId } : {}),
  ...(citySlug ? { citySlug } : {}),
  ...(poiSlug ? { poiSlug } : {}),
  ...(coordinates ? { coordinates: getCoordinates({ coordinates, cityId, citySlug, poiSlug }) } : {}),
  ...(country ? { country } : {}),
  ...(distance ? { distance } : {}),
  first: first ?? 20,
  ...(firstPois ? { firstPois } : {}),
  firstReviews: firstReviews ?? 48,
  locale: getLocaleFromLanguage(language),
  marketingPageEnabled: marketingPageEnabled ?? null,
  minReviewsRating: minReviewsRating ?? 4,
  minReviewsFeedbackLength: minReviewsFeedbackLength ?? 15,
  preferredLanguage: language,
  isOpen247: isOpen247 ?? null,
  ...(minRating ? { minRating } : {}),
  reviewOrderBy: [
    {
      field: ReviewOrderByField.FeedbackLength,
      direction: SortOrderDirection.Desc,
    },
    {
      field: ReviewOrderByField.Rating,
      direction: SortOrderDirection.Desc,
    },
    {
      field: ReviewOrderByField.CompletedAt,
      direction: SortOrderDirection.Desc,
    },
  ],
  ...(types?.length ? { types } : {}),
  withExternalReviews: false,
  withReservationCount: withReservationCount ?? false,
  orderBy: orderBy ?? StoreOrderBy.Relevance,
  initialSuggestions: initialSuggestions ?? false,
})
